/**
 * Geocode validation utilities
 * Validates various geocode formats before saving to ensure backend compatibility
 */

/**
 * Parse DMS (Degrees, Minutes, Seconds) format
 * Supports formats like: "37°45'24.3"N 113°02'59.2"W" or "37°45'24.3 N 113°02'59.2 W"
 * @param {string} dmsString - DMS format string
 * @returns {object|null} - { lat, lng } or null if invalid
 */
function parseDMS(dmsString) {
  // Remove extra spaces and normalize
  const normalized = dmsString.trim().replace(/\s+/g, ' ');

  // Regex to match DMS format: degrees°minutes'seconds"direction
  // Handles optional symbols and spaces
  const dmsRegex = /(-?\d+(?:\.\d+)?)[°\s]+(\d+(?:\.\d+)?)['\s]+(\d+(?:\.\d+)?)["\s]*([NSEW])/gi;

  const matches = [...normalized.matchAll(dmsRegex)];

  if (matches.length < 2) {
    return null; // Not valid DMS format
  }

  const coords = matches.map(match => {
    const degrees = parseFloat(match[1]);
    const minutes = parseFloat(match[2]);
    const seconds = parseFloat(match[3]);
    const direction = match[4].toUpperCase();

    // Convert to decimal degrees
    let decimal = degrees + (minutes / 60) + (seconds / 3600);

    // Apply negative for South and West
    if (direction === 'S' || direction === 'W') {
      decimal = -decimal;
    }

    return { decimal, direction };
  });

  // First coordinate should be latitude (N/S), second should be longitude (E/W)
  const latCoord = coords.find(c => c.direction === 'N' || c.direction === 'S');
  const lngCoord = coords.find(c => c.direction === 'E' || c.direction === 'W');

  if (!latCoord || !lngCoord) {
    return null;
  }

  return {
    lat: latCoord.decimal,
    lng: lngCoord.decimal
  };
}

/**
 * Validate and parse geocode string
 * @param {string} geocodeString - The geocode string to validate
 * @returns {object} - { valid: boolean, parsed: { lat, lng } | null, error: string | null }
 */
export function validateGeocodeFormat(geocodeString) {
  if (!geocodeString || !geocodeString.trim()) {
    return {
      valid: false,
      parsed: null,
      error: 'Geocode is required'
    };
  }

  const trimmed = geocodeString.trim();

  // Try parsing as JSON first
  try {
    const parsed = JSON.parse(trimmed);
    if (parsed.lat !== undefined && parsed.lng !== undefined) {
      const lat = parseFloat(parsed.lat);
      const lng = parseFloat(parsed.lng);
      if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        return {
          valid: true,
          parsed: { lat, lng },
          error: null
        };
      }
    }
    if (parsed.latitude !== undefined && parsed.longitude !== undefined) {
      const lat = parseFloat(parsed.latitude);
      const lng = parseFloat(parsed.longitude);
      if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        return {
          valid: true,
          parsed: { lat, lng },
          error: null
        };
      }
    }
  } catch (e) {
    // Not JSON, continue to other formats
  }

  // Try DMS format (check for degree symbol or directional indicators)
  if (trimmed.match(/[°'"NSEW]/i)) {
    const dmsResult = parseDMS(trimmed);
    if (dmsResult) {
      const { lat, lng } = dmsResult;
      if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        return {
          valid: true,
          parsed: { lat, lng },
          error: null
        };
      } else {
        return {
          valid: false,
          parsed: null,
          error: 'Coordinates out of valid range (latitude: -90 to 90, longitude: -180 to 180)'
        };
      }
    }
  }

  // Try comma-separated decimal format
  if (trimmed.includes(',')) {
    const parts = trimmed.split(',');
    if (parts.length === 2) {
      const lat = parseFloat(parts[0].trim());
      const lng = parseFloat(parts[1].trim());
      if (!isNaN(lat) && !isNaN(lng)) {
        if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
          return {
            valid: true,
            parsed: { lat, lng },
            error: null
          };
        } else {
          return {
            valid: false,
            parsed: null,
            error: 'Coordinates out of valid range (latitude: -90 to 90, longitude: -180 to 180)'
          };
        }
      }
    }
  }

  // No format matched
  return {
    valid: false,
    parsed: null,
    error: 'Supported formats: Decimal (37.7567,-113.0498) or DMS (37°45\'24"N 113°02\'59"W)'
  };
}

/**
 * Get a user-friendly description of supported geocode formats
 * @returns {string} - Description of supported formats
 */
export function getSupportedFormats() {
  return 'Supported formats:\n' +
         '• Decimal degrees: 37.7567,-113.0498\n' +
         '• DMS: 37°45\'24.3"N 113°02\'59.2"W\n' +
         '• JSON: {"lat": 37.7567, "lng": -113.0498}\n' +
         '• Google Maps URLs and Plus Codes are auto-converted';
}
